/**
 * PasswordStrength Component
 * 
 * Visual indicator for password strength based on validation requirements.
 * Shows real-time feedback as users type their password.
 * 
 * Requirements: 2.5
 */

import React from 'react';
import type { PasswordStrength as PasswordStrengthType } from '../types/auth.types';
import { validatePasswordStrength } from '../utils/validation.utils';

export interface PasswordStrengthProps {
  password: string;
  showRequirements?: boolean;
}

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({ 
  password, 
  showRequirements = true 
}) => {
  const strength: PasswordStrengthType = validatePasswordStrength(password);
  
  const getStrengthLabel = (score: number): string => {
    if (score === 0) return 'Very Weak';
    if (score === 1) return 'Weak';
    if (score === 2) return 'Fair';
    if (score === 3) return 'Good';
    return 'Strong';
  };
  
  const getStrengthColor = (score: number): string => {
    if (score === 0) return '#d32f2f';
    if (score === 1) return '#f57c00';
    if (score === 2) return '#fbc02d';
    if (score === 3) return '#7cb342';
    return '#388e3c';
  };

  if (!password) {
    return null;
  }

  return (
    <div 
      className="password-strength" 
      style={{ marginTop: '8px' }}
      role="region"
      aria-label="Password strength indicator"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div 
          role="progressbar"
          aria-valuenow={strength.score}
          aria-valuemin={0}
          aria-valuemax={4}
          aria-label={`Password strength: ${getStrengthLabel(strength.score)}`}
          style={{
            flex: 1,
            height: '4px',
            backgroundColor: '#e0e0e0',
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: `${(strength.score / 4) * 100}%`,
              height: '100%',
              backgroundColor: getStrengthColor(strength.score),
              transition: 'width 0.3s ease, background-color 0.3s ease',
            }}
          />
        </div>
        <span 
          aria-live="polite"
          style={{ 
            fontSize: '12px', 
            fontWeight: 500,
            color: getStrengthColor(strength.score),
          }}
        >
          {getStrengthLabel(strength.score)}
        </span>
      </div>

      {showRequirements && strength.feedback.length > 0 && (
        <ul 
          style={{ 
            margin: '8px 0 0 0', 
            padding: '0 0 0 20px',
            fontSize: '12px',
            color: '#666',
          }}
          aria-label="Password requirements"
        >
          {strength.feedback.map((item, index) => (
            <li key={index} style={{ marginBottom: '4px' }}>
              {item}
            </li>
          ))}
        </ul>
      )}
      
      {showRequirements && strength.isValid && (
        <div 
          role="status"
          aria-live="polite"
          style={{ 
            fontSize: '12px', 
            color: '#2e7d32',
            marginTop: '8px',
            fontWeight: 500,
          }}
        >
          ✓ Password meets all requirements
        </div>
      )}
    </div>
  );
};
